import { Location } from "@remix-run/react";
import { allCookieCategories } from "~/application/cookies/ApplicationCookies";
import { CookieCategory } from "~/application/cookies/CookieCategory";
import { UserSession } from "../session.server";

/**
 * Determines if cookies should be marked as secure.
 * Returns true if the application is running over HTTPS or in production.
 * This helps prevent cookies from being transmitted over insecure connections.
 */
function shouldUseSecureCookies(request?: Request): boolean {
  // Always use secure cookies in production
  if (process.env.NODE_ENV === "production") {
    return true;
  }
  
  // Check if we're explicitly running in a secure context
  if (process.env.HTTPS === "true" || process.env.FORCE_HTTPS === "true") {
    return true;
  }
  
  // Check for common deployment platforms that use HTTPS
  if (process.env.FLY_APP_NAME || process.env.VERCEL_URL || process.env.HEROKU_APP_NAME) {
    return true;
  }
  
  // Check for any environment that indicates a non-development setup
  const env = process.env.NODE_ENV?.toLowerCase() || "";
  if (env.includes("test") || env.includes("staging") || env.includes("qa") || env.includes("prod")) {
    return true;
  }
  
  // If we have a request object, check if it's HTTPS
  if (request) {
    const url = new URL(request.url);
    if (url.protocol === "https:") {
      return true;
    }
    
    // Check common HTTPS headers from reverse proxies
    const proto = request.headers.get("x-forwarded-proto");
    const scheme = request.headers.get("x-forwarded-scheme");
    if (proto === "https" || scheme === "https") {
      return true;
    }
    
    // Check if the request is secure (common in cloud environments)
    if (request.headers.get("x-forwarded-ssl") === "on") {
      return true;
    }
  }
  
  // For development on localhost, only use secure cookies if explicitly enabled
  // This allows for local development while still being secure by default
  return false;
}

function hasConsent(userSession: UserSession, category: CookieCategory) {
  return category === CookieCategory.REQUIRED || userSession?.cookies?.find((f) => f.category === CookieCategory[category])?.allowed;
}

function requiresCookieConsent(userSession: UserSession, searchParams: URLSearchParams) {
  if (!userSession.cookies || userSession.cookies.length === 0 || searchParams.get("cookies")) {
    return true;
  }

  const requiredCookieCategoryConsent = allCookieCategories.map((cookieCategory) => {
    if (userSession.cookies.find((f) => f.category === CookieCategory[cookieCategory]) === undefined) {
      return cookieCategory;
    }
    return null;
  });
  if (requiredCookieCategoryConsent.filter((f) => f).length > 0) {
    return true;
  }

  return false;
}

function getUpdateCookieConsentForm({
  location,
  selectedCookies,
  searchParams,
}: {
  location: Location;
  selectedCookies: CookieCategory[];
  searchParams: URLSearchParams;
}) {
  const form = new FormData();
  form.set("action", "setCookieConsent");
  form.set("redirect", location.pathname);
  form.set("ref", searchParams.get("ref")?.toString() ?? "");
  form.set("source", searchParams.get("source")?.toString() ?? "");
  form.set("utm_source", searchParams.get("utm_source")?.toString() ?? "");
  form.set("utm_medium", searchParams.get("utm_medium")?.toString() ?? "");
  form.set("utm_campaign", searchParams.get("utm_campaign")?.toString() ?? "");
  form.set("utm_content", searchParams.get("utm_content")?.toString() ?? "");
  form.set("utm_term", searchParams.get("utm_term")?.toString() ?? "");
  selectedCookies.forEach((cookie) => {
    form.append("cookies[]", CookieCategory[cookie]);
  });
  return form;
}

export default {
  shouldUseSecureCookies,
  hasConsent,
  requiresCookieConsent,
  getUpdateCookieConsentForm,
};
